import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { quizDraftReducer, QUIZ_DRAFT_ACTION } from 'pages/CreateQuiz/quizReducer';
import { createContext, useCallback, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import apiService from 'services/api';
import quizService from 'services/quiz';
import { buildQuizPayload, createQuizDraft, hasCompleteQuestionCriteria } from 'utils/quizDraft';
import { getQuizOverview } from 'utils/quizOverview';
import { hasQuizRubric } from 'utils/quizRubric';
import { v4 as uuid } from 'uuid';

export const PageContext = createContext();

const PageProvider = ({ children }) => {
    const history = useHistory();
    const { t } = useTranslation(['common', 'assignment', 'quiz']);
    const { auth } = useAuth();
    const { course, isFeedbackFeatureDisabled, setQuizzes } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { state } = useLocation();
    const [draft, dispatch] = useReducer(
        quizDraftReducer,
        { quiz: state?.quiz, courseId: auth?.course_id },
        ({ quiz, courseId }) => createQuizDraft(quiz, courseId)
    );
    const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
    const [rubricDialog, setRubricDialog] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isResyncing, setIsResyncing] = useState(false);
    const { questions, step, form, rubricNeedsSync } = draft;
    const quiz = useMemo(() => (draft.quiz ? { ...draft.quiz, questions } : undefined), [draft.quiz, questions]);
    const overview = useMemo(() => getQuizOverview(quiz), [quiz]);
    const setForm = useCallback((update) => dispatch({ type: QUIZ_DRAFT_ACTION.UPDATE_FORM, update }), []);
    const canContinue =
        Boolean(quiz) &&
        !isSaving &&
        (step === 1 ||
            (hasQuizRubric(form.rubric) && (step !== 3 || hasCompleteQuestionCriteria(questions, form.rubricType))));

    const setStep = (nextStep) => dispatch({ type: QUIZ_DRAFT_ACTION.SET_STEP, step: nextStep });

    const handleBack = () => {
        if (step > 1) {
            setStep(step - 1);
            return;
        }
        setDialog({
            open: true,
            message: t('dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
            description: t(
                'dialog.unsaved-changes.description',
                'If you exit without saving, all unsaved changes will be lost.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('controls.exit', 'Exit'),
            cancelButtonText: t('controls.cancel', 'Cancel'),
            onAction: () => {
                history.push('/quizzes');
                closeDialog();
            },
            onCancel: closeDialog,
            onClose: closeDialog,
        });
    };

    const handleNext = () => {
        if (!quiz) return;
        if (step === 1) {
            setStep(2);
            return;
        }
        if (!hasQuizRubric(form.rubric)) return;
        if (rubricNeedsSync) {
            setRubricDialog('sync');
            return;
        }
        setStep(3);
    };

    const handleRubricTypeChange = (rubricType) => {
        dispatch({ type: QUIZ_DRAFT_ACTION.SET_RUBRIC_TYPE, rubricType });
    };

    const handleSaveRubric = (rubric) => {
        dispatch({ type: QUIZ_DRAFT_ACTION.SAVE_RUBRIC, rubric });
    };

    const handleDeleteRubric = () => {
        setDialog({
            open: true,
            message: t('quiz:dialog.delete-rubric.title', 'Delete Rubric?'),
            description: t('quiz:dialog.delete-rubric.description', 'This action cannot be undone.'),
            variant: 'destructive',
            actionButtonText: t('controls.delete', 'Delete'),
            cancelButtonText: t('controls.cancel', 'Cancel'),
            onAction: () => {
                dispatch({ type: QUIZ_DRAFT_ACTION.DELETE_RUBRIC });
                closeDialog();
            },
            onCancel: closeDialog,
            onClose: closeDialog,
        });
    };

    const handleSyncedRubric = ({ api_data, rubric }) => {
        dispatch({ type: QUIZ_DRAFT_ACTION.SYNC_RUBRIC, api_data, rubric });
        setRubricDialog(null);
    };

    const handleMapCriteriaByOrderChange = (checked) => {
        dispatch({ type: QUIZ_DRAFT_ACTION.SET_MAP_CRITERIA_BY_ORDER, checked });
    };

    const handleCriterionIdsChange = (questionId, criterionIds) => {
        dispatch({
            type: QUIZ_DRAFT_ACTION.UPDATE_QUESTION,
            questionId,
            changes: { criterion_ids: criterionIds },
        });
    };

    const handleGradingNotesChange = (questionId, notes) => {
        dispatch({
            type: QUIZ_DRAFT_ACTION.UPDATE_QUESTION,
            questionId,
            changes: { grading_notes: notes },
        });
    };

    const handleResyncQuiz = async () => {
        if (!auth?.course_id || !form.api_assignment_id || isResyncing) return;
        setIsResyncing(true);
        try {
            const syncedQuiz = await apiService.getQuiz({
                access_token: localStorage.getItem('access_token'),
                course_id: auth.course_id,
                quiz_id: form.api_assignment_id,
            });
            if (!syncedQuiz || !Array.isArray(syncedQuiz.questions)) throw new Error('Invalid LMS quiz details');
            dispatch({
                type: QUIZ_DRAFT_ACTION.RESYNC_QUIZ,
                quiz: syncedQuiz,
                courseId: auth.course_id,
            });
            message.success(t('quiz:message.resync-success', 'Quiz questions were resynced from your LMS.'));
        } catch {
            message.error(t('quiz:message.resync-error', 'Quiz questions could not be resynced. Please try again.'));
        } finally {
            setIsResyncing(false);
        }
    };

    const handleSaveQuiz = async () => {
        if (!canContinue || step !== 3 || isSaving) return;
        setIsSaving(true);
        const timestamp = new Date().getTime();
        const courseId = course?.id || course?.course_id || auth?.course_id;
        const savedQuiz = buildQuizPayload({
            draft,
            id: uuid(),
            timestamp,
            ownerAccountId: auth?.id,
            courseId,
            isFeedbackFeatureDisabled,
        });

        try {
            const createdQuiz = (await quizService.createQuiz(courseId, savedQuiz)) || savedQuiz;
            setQuizzes((current) => [...(current || []), createdQuiz]);
            message.success(t('quiz:message.create-success', 'Your quiz was successfully saved.'));
            history.push('/quizzes');
        } catch {
            message.error(t('quiz:message.create-error', 'The quiz could not be saved. Please try again.'));
            setIsSaving(false);
        }
    };

    const handleSaveFeedback = ({ feedbackCustomContext, ...feedbackSettings }) => {
        dispatch({
            type: QUIZ_DRAFT_ACTION.SAVE_FEEDBACK,
            feedbackSettings,
            feedbackCustomContext,
        });
    };

    return (
        <PageContext.Provider
            value={{
                quiz,
                questions,
                overview,
                step,
                form,
                setForm,
                canContinue,
                isSaving,
                isResyncing,
                rubricDialog,
                setRubricDialog,
                handleRubricTypeChange,
                handleMapCriteriaByOrderChange,
                handleCriterionIdsChange,
                handleGradingNotesChange,
                handleResyncQuiz,
                handleSaveRubric,
                handleDeleteRubric,
                handleSyncedRubric,
                feedbackDialogOpen,
                setFeedbackDialogOpen,
                handleSaveFeedback,
                handleBack,
                handleNext,
                handleSaveQuiz,
            }}
        >
            {children}
        </PageContext.Provider>
    );
};

export default PageProvider;
